import { Form } from 'react-bootstrap';
import { number, oneOfType, string } from 'prop-types';
import ProductReviewsListItem from './ProductReviewsListItem';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from './context/ToastContext';
import '../styles/ProductReviewsList.css';
import constants from '../constants.json' assert { type: 'json' };


/** Configuration */
const SORT_ORDERS = Object.freeze({
    HIGHEST_RATING: {
        LABEL: 'Rating: high to low',
        VALUE: 'highestrating',
    },
    LOWEST_RATING: {
        LABEL: 'Rating: low to high',
        VALUE: 'lowestrating',
    },
    NEWEST: {
        LABEL: 'Newest',
        VALUE: 'newest',
    }
});
const DEFAULT_LIMIT = 10;
const { REVIEW } = constants;
/**
 * Display a list of reviews.
 * @component
 */
const ProductReviewsList = ({ productId }) => {
    /** Contexts */
    const { sendMessage } = useContext(ToastContext);

    /** Refs */
    const containerRef = useRef(null);

    /** States */
    const [isLoaded, setIsLoaded] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [sortOrder, setSortOrder] = useState(SORT_ORDERS.HIGHEST_RATING);

    /** Event Handlers */
    const handleSortOrderChange = (e) => setSortOrder(e.target.value);

    // Fetch the reviews for the product.
    const fetchReviews = useCallback(async () => {
        try {
            setIsLoaded(false);
            const params = new URLSearchParams({
                page: 0,
                limit: DEFAULT_LIMIT,
                productId,
                sort: sortOrder,
            });
            const url = new URL(`/api/reviews?${params}`, process.env.APP_URL);
            const response = await fetch(url, {
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await response.json();
            if (!data.reviews) {
                throw new Error('Failed to fetch reviews');
            }
            setReviews(data.reviews);
            setIsLoaded(true);
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, [productId, sortOrder]);

    // Fetch reviews when callback changes.
    useEffect(() => {
        fetchReviews();
    }, [ fetchReviews ]);

    return <div
        className={'product-reviews-list--container'}>
        {Array.isArray(reviews) && reviews.length > 1 &&
            <div
                className={'product-reviews-list--sort-selection'}>
                <span>Sort reviews by:</span>
                <Form.Select
                    value={sortOrder}
                    onChange={handleSortOrderChange}>
                    {Object.keys(SORT_ORDERS).map((key) => <option
                        key={key}
                        value={SORT_ORDERS[key].VALUE}>
                        {SORT_ORDERS[key].LABEL}
                    </option>)}
                </Form.Select>
            </div>}
        {isLoaded === true
            ? !Array.isArray(reviews) || reviews.length < 1
                ? <p
                    className={'product-reviews-list--no-content'}>
                    {REVIEW.NO_PRODUCT_REVIEW_MESSAGE}
                </p>
                : <div
                    className={'product-reviews-list--list-container'}
                    ref={containerRef}>
                    {reviews.map(
                        (review, index) => <ProductReviewsListItem
                            key={index}
                            review={review}
                        />
                    )}
                </div>
            : <div
                className={'product-reviews-list--loader'}
                style={{
                    height: containerRef.current?.clientHeight || 0,
                    width: containerRef.current?.clientWidth || 0,
                }}>
            </div>
        }
    </div>;
};

/** Prop Types */
ProductReviewsList.propTypes = {
    productId: oneOfType([number, string]),
};

export default ProductReviewsList;